import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Gift, Star, Trophy, Users, Copy, CheckCircle, Crown, Award, TrendingUp, RefreshCw, Globe, Loader2 } from 'lucide-react';
import { formatDateIST } from '@/lib/timeUtils';

interface LoyaltyProgram {
  id: number;
  clientId: number;
  tier: string;
  totalPoints: number;
  availablePoints: number;
  totalSpent: string;
  eventsCompleted: number;
  referralCode: string;
  joinedAt: string;
  domainPointsRedeemed?: boolean;
  domainPointsRedeemedAt?: string;
}

interface LoyaltyTransaction {
  id: number;
  type: string;
  points: number;
  description: string;
  createdAt: string;
}

interface LoyaltyReward {
  id: number;
  name: string;
  description: string;
  pointsCost: number;
  discountType: string;
  discountValue: string;
  category: string;
  minTier: string;
}

export default function LoyaltyProgram() {
  const [copiedCode, setCopiedCode] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch loyalty program data
  const { data: program, isLoading: programLoading } = useQuery<LoyaltyProgram>({
    queryKey: ['/api/loyalty/program'],
  });

  // Fetch transactions
  const { data: transactions = [], isLoading: transactionsLoading } = useQuery<LoyaltyTransaction[]>({
    queryKey: ['/api/loyalty/transactions'],
  });

  // Fetch rewards
  const { data: rewards = [], isLoading: rewardsLoading } = useQuery<LoyaltyReward[]>({
    queryKey: ['/api/loyalty/rewards'],
  });

  // Redeem reward mutation
  const redeemMutation = useMutation({
    mutationFn: async (data: { rewardId: number; weddingId?: number }) => {
      return await apiRequest('/api/loyalty/redeem', {
        method: 'POST',
        data: data,
      });
    },
    onSuccess: () => {
      toast({
        title: "Reward Redeemed",
        description: "Your reward has been successfully redeemed!",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/loyalty/program'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loyalty/transactions'] });
    },
    onError: (error: any) => {
      toast({
        title: "Redemption Failed",
        description: error.message || "Failed to redeem reward",
        variant: "destructive",
      });
    },
  });

  // Redeem points for domain mutation
  const redeemForDomainMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('/api/loyalty/redeem-for-domain', {
        method: 'POST',
      });
    },
    onSuccess: () => {
      toast({
        title: "Points Redeemed!",
        description: "You can now get a free custom domain for your wedding website!",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/loyalty/program'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loyalty/transactions'] });
    },
    onError: (error: any) => {
      toast({
        title: "Redemption Failed",
        description: error.message || "Failed to redeem points for domain",
        variant: "destructive",
      });
    },
  });

  const DOMAIN_POINTS_COST = 500;
  const canRedeemForDomain = program && !program.domainPointsRedeemed && (program.availablePoints || 0) >= DOMAIN_POINTS_COST;

  const copyReferralCode = () => {
    if (program?.referralCode) {
      navigator.clipboard.writeText(program.referralCode);
      setCopiedCode(true);
      toast({
        title: "Copied!",
        description: "Referral code copied to clipboard",
      });
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier?.toLowerCase()) {
      case 'bronze': return 'bg-amber-500/20 text-amber-300 border-amber-500';
      case 'silver': return 'bg-slate-500/20 text-slate-300 border-slate-500';
      case 'gold': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500';
      case 'platinum': return 'bg-purple-500/20 text-purple-300 border-purple-500';
      default: return 'bg-slate-500/20 text-slate-300 border-slate-500';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'photography': return '📸';
      case 'catering': return '🍽️';
      case 'decoration': return '🎨';
      case 'venue': return '🏛️';
      default: return '🎁';
    }
  };

  if (programLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4"></div>
            <p className="text-slate-300">Loading your loyalty program...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header Section */}
      <div className="border-b border-slate-700 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                  <Crown className="w-7 h-7 text-slate-900" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Loyalty Program</h1>
                  <p className="text-slate-400">Rewards and exclusive benefits</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <Badge className={`px-4 py-2 text-base font-semibold ${getTierColor(program?.tier || 'bronze')}`}>
                {program?.tier?.toUpperCase() || 'BRONZE'} MEMBER
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm shadow-xl">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs sm:text-sm font-medium">Available Points</p>
                  <p className="text-xl sm:text-3xl font-bold text-white mt-1">{program?.availablePoints || 0}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm shadow-xl">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs sm:text-sm font-medium">Total Earned</p>
                  <p className="text-xl sm:text-3xl font-bold text-white mt-1">{program?.totalPoints || 0}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm shadow-xl">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs sm:text-sm font-medium">Events Completed</p>
                  <p className="text-xl sm:text-3xl font-bold text-white mt-1">{program?.eventsCompleted || 0}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm shadow-xl">
            <CardContent className="p-3 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-xs sm:text-sm font-medium">Total Spent</p>
                  <p className="text-xl sm:text-3xl font-bold text-white mt-1">₹{program?.totalSpent || '0'}</p>
                </div>
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="rewards" className="space-y-6">
          <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="inline-flex w-max min-w-full sm:grid sm:w-full sm:grid-cols-3 bg-slate-800/50 border border-slate-700 shadow-lg backdrop-blur-sm">
            <TabsTrigger value="rewards" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-300 font-semibold text-sm py-3 transition-all hover:bg-slate-700/50 flex-shrink-0">
              Available Rewards
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-300 font-semibold text-sm py-3 transition-all hover:bg-slate-700/50 flex-shrink-0">
              Transaction History
            </TabsTrigger>
            <TabsTrigger value="referrals" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900 text-slate-300 font-semibold text-sm py-3 transition-all hover:bg-slate-700/50 flex-shrink-0">
              Refer Friends
            </TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="rewards" className="space-y-6">
            {/* Featured Reward: Free Custom Domain */}
            <Card className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-purple-500/50 shadow-xl backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-purple-500/30 rounded-full flex items-center justify-center">
                      <Globe className="w-7 h-7 text-purple-300" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        Free Custom Domain
                        <Badge className="bg-purple-500/30 text-purple-200 border-purple-400">
                          FEATURED
                        </Badge>
                      </h3>
                      <p className="text-purple-200 text-sm mt-1">
                        Get a custom domain for your wedding website (e.g., john-and-jane.com)
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 w-full sm:w-auto">
                    <div className="text-2xl font-bold text-purple-200">
                      {DOMAIN_POINTS_COST} Points
                    </div>
                    {program?.domainPointsRedeemed ? (
                      <div className="flex items-center gap-2 text-emerald-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">Already Redeemed</span>
                      </div>
                    ) : canRedeemForDomain ? (
                      <Button
                        onClick={() => redeemForDomainMutation.mutate()}
                        disabled={redeemForDomainMutation.isPending}
                        className="bg-purple-500 hover:bg-purple-600 text-white font-semibold w-full sm:w-auto"
                      >
                        {redeemForDomainMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Redeeming...
                          </>
                        ) : (
                          <>
                            <Gift className="w-4 h-4 mr-2" />
                            Redeem Now
                          </>
                        )}
                      </Button>
                    ) : (
                      <div className="text-red-400 text-sm text-right">
                        Need {DOMAIN_POINTS_COST - (program?.availablePoints || 0)} more points
                      </div>
                    )}
                  </div>
                </div>
                {program?.domainPointsRedeemed && program.domainPointsRedeemedAt && (
                  <p className="text-purple-300 text-xs mt-3">
                    Redeemed on {formatDateIST(program.domainPointsRedeemedAt)}
                  </p>
                )}
                {!program?.domainPointsRedeemed && (
                  <p className="text-purple-300/70 text-xs mt-3">
                    This reward can only be redeemed once. After redemption, set up your custom domain from the website settings.
                  </p>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {rewardsLoading ? (
                <div className="col-span-full text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-4"></div>
                  <p className="text-slate-300">Loading rewards...</p>
                </div>
              ) : rewards.length === 0 ? (
                <div className="col-span-full text-center py-8">
                  <Gift className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-300">No rewards available at the moment</p>
                </div>
              ) : (
                rewards.map((reward) => (
                  <Card key={reward.id} className="bg-slate-800/50 border-slate-700 hover:border-amber-500 transition-all duration-300 shadow-xl hover:shadow-2xl backdrop-blur-sm">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">{getCategoryIcon(reward.category)}</span>
                          <div>
                            <CardTitle className="text-lg text-white">{reward.name}</CardTitle>
                            <Badge variant="outline" className="text-xs bg-emerald-900/50 text-emerald-300 border-emerald-600">
                              {reward.category}
                            </Badge>
                          </div>
                        </div>
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500">
                          {reward.minTier.toUpperCase()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-300 text-sm mb-4">{reward.description}</p>
                      <div className="flex justify-between items-center">
                        <div className="text-lg font-bold text-amber-400">
                          {reward.pointsCost} Points
                        </div>
                        <Button
                          onClick={() => redeemMutation.mutate({ rewardId: reward.id })}
                          disabled={
                            (program?.availablePoints || 0) < reward.pointsCost ||
                            redeemMutation.isPending
                          }
                          className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold disabled:bg-slate-600 disabled:text-slate-400"
                        >
                          {redeemMutation.isPending ? 'Redeeming...' : 'Redeem'}
                        </Button>
                      </div>
                      {(program?.availablePoints || 0) < reward.pointsCost && (
                        <p className="text-red-400 text-xs mt-2">
                          Need {reward.pointsCost - (program?.availablePoints || 0)} more points
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="transactions" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700 shadow-xl backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <RefreshCw className="w-5 h-5 text-amber-500" />
                  <span>Transaction History</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactionsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto mb-4"></div>
                    <p className="text-slate-300">Loading transactions...</p>
                  </div>
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <Star className="h-16 w-16 text-slate-500 mx-auto mb-4" />
                    <p className="text-slate-300">No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="flex justify-between items-center p-4 border border-slate-600 rounded-lg bg-slate-900/50">
                        <div>
                          <p className="font-medium text-white">{transaction.description}</p>
                          <p className="text-sm text-slate-400">
                            {formatDateIST(transaction.createdAt)}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`font-bold ${transaction.points > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {transaction.points > 0 ? '+' : ''}{transaction.points} points
                          </span>
                          <Badge variant="outline" className="ml-2 text-xs bg-amber-500/20 text-amber-300 border-amber-500">
                            {transaction.type}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals" className="space-y-6">
            <Card className="bg-slate-800/50 border-slate-700 shadow-xl backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <Users className="h-6 w-6 text-amber-500" />
                  <span>Refer Friends & Earn Points</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-slate-900/50 p-6 border border-slate-600 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 text-white">Your Referral Code</h3>
                  <div className="flex items-center space-x-3">
                    <code className="bg-amber-500 text-slate-900 px-4 py-2 rounded text-lg font-mono border flex-1 font-bold">
                      {program?.referralCode || 'Loading...'}
                    </code>
                    <Button
                      onClick={copyReferralCode}
                      variant="outline"
                      className="flex items-center space-x-2 border-amber-500 text-amber-400 hover:bg-amber-500/10"
                      disabled={!program?.referralCode}
                    >
                      {copiedCode ? (
                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                      <span>{copiedCode ? 'Copied!' : 'Copy'}</span>
                    </Button>
                  </div>
                  <p className="text-center text-lg font-semibold text-emerald-400 mt-4">
                    Start sharing and earning today!
                  </p>
                </div>

                <div className="bg-gradient-to-r from-amber-900/30 to-amber-800/30 p-6 rounded-lg border border-amber-600/30">
                  <h3 className="text-lg font-semibold mb-4 text-white flex items-center">
                    <Trophy className="w-5 h-5 mr-2 text-amber-500" />
                    Earn Points with LA Mirage Referrals!
                  </h3>
                  <p className="text-slate-300 mb-4">
                    Spread the word and earn rewards when your friends book their first event at LA Mirage Koratty! Here's how it works:
                  </p>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-center"><Star className="w-4 h-4 mr-2 text-amber-500" /> Share your referral code with friends & family.</li>
                    <li className="flex items-center"><Gift className="w-4 h-4 mr-2 text-amber-500" /> When they book their first event, you both earn points:</li>
                    <li className="ml-6 text-emerald-400">• You get 750 points</li>
                    <li className="ml-6 text-emerald-400">• They get 250 welcome points</li>
                    <li className="flex items-center"><RefreshCw className="w-4 h-4 mr-2 text-amber-500" /> No limit on referrals—the more you refer, the more you earn!</li>
                  </ul>
                </div>

                <div className="bg-blue-900/30 p-6 border border-blue-600/30 rounded-lg">
                  <h3 className="text-lg font-semibold mb-3 text-white">How to book with your code?</h3>
                  <ul className="space-y-2 text-slate-300 mb-4">
                    <li>• Your friends book online at: <span className="text-amber-400 font-semibold">https://la-mirage.in/booking</span></li>
                    <li>• They enter your referral code during checkout.</li>
                  </ul>
                  <div className="bg-amber-900/30 border border-amber-600/30 p-4 rounded-lg">
                    <p className="text-amber-300 text-sm font-medium">
                      Share your code today and start earning rewards together!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}